import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import { api } from "../../lib/api";

// Path-gated super admin: URL must be /admin/<SUPER_ADMIN_PATH>/login (and /dashboard).
// We verify the path with a backend check before showing the login form.
export default function AdminApp() {
  return (
    <Routes>
      <Route path=":secret/login" element={<AdminLogin />} />
      <Route path=":secret/dashboard" element={<AdminDashboard />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function NotFound() {
  return <div className="min-h-screen grid place-items-center text-ink-500">Not found.</div>;
}

function AdminLogin() {
  const { secret } = useParams<{ secret: string }>();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get<{ ok: boolean }>(`/admin/path-check?p=${encodeURIComponent(secret ?? "")}`)
      .then((r) => setAllowed(r.ok))
      .catch(() => setAllowed(false));
  }, [secret]);

  if (allowed === null) return <div className="min-h-screen grid place-items-center text-ink-500">…</div>;
  if (!allowed) return <NotFound />;

  return (
    <div className="min-h-screen grid place-items-center bg-ink-900">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true); setErr(null);
          try {
            await api.post("/admin/login", { username, password });
            nav(`/admin/${secret}/dashboard`);
          } catch (e: any) { setErr(e.message); }
          finally { setBusy(false); }
        }}
        className="card p-6 w-full max-w-sm"
      >
        <div className="text-center mb-6">
          <div className="text-xl font-semibold">Owner Console</div>
          <div className="text-xs text-ink-500">Super Admin Access</div>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label">Username</label>
            <input className="input" autoFocus value={username} onChange={(e) => setUsername(e.target.value)} />
          </div>
          <div>
            <label className="label">Password</label>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {err && <div className="text-sm text-brand">{err}</div>}
          <button className="btn-primary w-full" disabled={busy}>{busy ? "…" : "Sign in"}</button>
        </div>
      </form>
    </div>
  );
}

function AdminDashboard() {
  const { secret } = useParams<{ secret: string }>();
  const nav = useNavigate();
  const [stats, setStats] = useState<{ organizations: number; users: number; people: number; groups: number } | null>(null);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      const [s, o] = await Promise.all([
        api.get<any>("/admin/stats"),
        api.get<{ organizations: any[] }>("/admin/orgs"),
      ]);
      setStats(s); setOrgs(o.organizations);
    } catch (e: any) {
      setErr(e.message);
      // Not authed → bounce back to login.
      nav(`/admin/${secret}/login`);
    }
  };
  useEffect(() => { load(); }, []);

  if (err) return <div className="min-h-screen grid place-items-center text-brand">{err}</div>;
  if (!stats) return <div className="min-h-screen grid place-items-center text-ink-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-ink-50">
      <header className="bg-ink-900 text-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="font-semibold">Owner Console</div>
          <button className="text-sm text-white/80 hover:text-white" onClick={async () => {
            await api.post("/admin/logout"); nav(`/admin/${secret}/login`);
          }}>Sign out</button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        <h1 className="text-2xl font-semibold">Platform overview</h1>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Mini label="Organisations" value={stats.organizations} />
          <Mini label="Users" value={stats.users} />
          <Mini label="People" value={stats.people} />
          <Mini label="Groups" value={stats.groups} />
        </div>

        <section className="card overflow-hidden">
          <div className="px-5 py-4 border-b border-ink-200 flex items-center justify-between">
            <h2 className="font-semibold">Organisations</h2>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-left text-xs font-medium uppercase text-ink-500">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Slug</th>
                <th className="px-5 py-3">Users</th>
                <th className="px-5 py-3">People</th>
                <th className="px-5 py-3">Groups</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {orgs.map((o) => (
                <tr key={o.id}>
                  <td className="px-5 py-3 font-medium">{o.name}</td>
                  <td className="px-5 py-3 text-ink-500">{o.slug}</td>
                  <td className="px-5 py-3">{o.user_count}</td>
                  <td className="px-5 py-3">{o.people_count}</td>
                  <td className="px-5 py-3">{o.group_count}</td>
                  <td className="px-5 py-3 text-ink-500">{new Date(o.created_at * 1000).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        className="text-ink-600 hover:text-ink-900 text-xs"
                        onClick={async () => {
                          const force = (o.people_count ?? 0) >= 5;
                          const msg = force
                            ? `"${o.name}" already has data. Seed more sample data anyway?`
                            : `Seed "${o.name}" with sample data (3 leaders, 2 groups, 12 people, 4 leads)?`;
                          if (!confirm(msg)) return;
                          try {
                            await api.post(`/admin/orgs/${o.id}/seed${force ? "?force=1" : ""}`);
                            load();
                            alert("Sample data seeded.");
                          } catch (e: any) {
                            alert(e.message);
                          }
                        }}
                        title="Seed this org with realistic sample data"
                      >
                        Seed
                      </button>
                      <button className="text-brand text-xs" onClick={async () => {
                        if (!confirm(`Delete org "${o.name}" and ALL its data? This cannot be undone.`)) return;
                        await api.del(`/admin/orgs/${o.id}`);
                        load();
                      }}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {orgs.length === 0 && <tr><td colSpan={7} className="px-5 py-12 text-center text-ink-400">No organisations yet.</td></tr>}
            </tbody>
          </table>
        </section>
      </main>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: number }) {
  return (
    <div className="card p-4">
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-xs text-ink-500 mt-1">{label}</div>
    </div>
  );
}
